import React from 'react';
import { Progress } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAddressCard, faFolderOpen, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    indexCellRenderer,
    TextFilterDefaults,
    LinkCellRenderer,
    ButtonCellRenderer,
    PermissionButtonCellRenderer,
    DateFilterDefaults,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import CommonContext, { ApiRoutes, ApplicationPermissions, AppNavPaths } from '../Common';
import { PageHeading, PageWrap } from '../common/forms/FormElements';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import authService from '../api-authorization/AuthorizeService';
import { ContractStatus, _ContractStatuses } from './Contract';
import './ContractIndex.scss';

const getButtonLabel = (id, [isProjectContract, , hasJobRelatedCustomForms]) => {
    if (isProjectContract && !hasJobRelatedCustomForms) return 'View Projects';
    return 'New Job';
};

const getIcon = (id, [isProjectContract, , hasJobRelatedCustomForms]) => {
    if (isProjectContract && !hasJobRelatedCustomForms) return faFolderOpen;
    return faPlusCircle;
};

const quickRentalOptions = [
    { label: 'Yes', value: 'true' },
    { label: 'No', value: 'false' },
];

class ContractIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            rowData: [],
            selectedRow: null,
        };
        this.onAddJob = this.onAddJob.bind(this);
        this.onViewProjects = this.onViewProjects.bind(this);
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        void this.populateState();
    }

    onAddJob = (id, [isProjectContract, contractNumber, hasJobRelatedCustomForms]) => {
        const useProjects = isProjectContract && !hasJobRelatedCustomForms;
        this.context.history.push(useProjects ? `${AppNavPaths.Projects}?n=${contractNumber}&i=${id}` : `${AppNavPaths.JobNew}/${id}`);
    };

    onViewProjects = (id, [_, contractNumber]) => {
        this.context.history.push(`${AppNavPaths.Projects}?n=${contractNumber}&i=${id}`);
    };

    async populateState() {
        const { user } = this.context;

        const gridPermissions = await httpClient.get(
            ApiRoutes.contract.getGridPermissions(),
        );

        const gridOptions = createGridOptions(this);

        gridOptions.components = {
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
            nameRenderer: LinkCellRenderer,
            buttonRenderer: ButtonCellRenderer,
            permissionButtonRenderer: PermissionButtonCellRenderer,
        };

        const [dispatchLocations] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.dispatchLocations()),
        ]);

        // Set the user's initial dispatch selection to their assigned dispatch, if any.
        // Due to an enhancement, the user may see more than one dispatch, this is determined
        // at the server level and by role.
        let initialDispatchValue = null;
        if ((dispatchLocations || []).length === 1) {
            initialDispatchValue = dispatchLocations[0].value;
        } else if (!user.isAdmin) {
            initialDispatchValue = user.dispatchCompanyId;
        }

        const dispatchFilterParams = {
            suppressFilterButton: true,
            options: dispatchLocations,
            optionsLabel: 'label',
            optionsValue: 'value',
            disableFilter: (dispatchLocations || []).length === 1,
            initialFilterValue: initialDispatchValue,
        };

        gridOptions.columnDefs = [
            {
                flex: 0,
                maxWidth: 120,
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                cellRenderer: indexCellRenderer,
            },
            {
                headerName: 'Description',
                field: 'description',
                sortable: true,
                sort: { direction: 'asc', priority: 0 },
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellRenderer: gridPermissions.canEditContract
                    ? 'nameRenderer'
                    : null,
                cellRendererParams: {
                    to: (id) => `${AppNavPaths.Contract}/${id}`,
                    nameField: 'description',
                    idField: 'id',
                    title: 'View this Contract',
                },
            },
            {
                colId: 'Company.CompanyName',
                headerName: 'Customer Name',
                field: 'customerName',
                sortable: true,

                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Number',
                headerName: 'Contract #',
                field: 'number',
                sortable: true,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'OperationsCenterCompany.CompanyName',
                headerName: 'Operations Center',
                field: 'operationsCenter',
                sortable: true,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'ContractLocations.LocationId',
                headerName: 'Dispatching',
                sortable: false,
                minWidth: 220,
                flex: 1,
                field: 'dispatchingFlat',
                tooltipField: 'dispatchingFlat',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: { ...dispatchFilterParams },
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: { ...dispatchFilterParams },
            },
            {
                colId: 'ContractStatus.Description',
                flex: 0,
                maxWidth: 120,
                headerName: 'Status',
                field: 'status',
                sortable: true,
                filter: 'selectFilter',
                filterParams: {
                    suppressMenu: true,
                    floatingFilter: true,
                    labelText: 'Filter by status',
                    options: ContractStatus,
                    optionsLabel: 'description',
                    optionsValue: 'id',
                    initialFilterValue: _ContractStatuses.Active,
                },
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                    options: ContractStatus,
                    optionsLabel: 'description',
                    optionsValue: 'id',
                    initialFilterValue: _ContractStatuses.Active,
                },
            },
        ];

        const enableContractDateRange = Boolean(
            this.context.tenant?.tenantSettings?.enableContractDateRange,
        );

        if (enableContractDateRange) {
            gridOptions.columnDefs.push({
                colId: 'EffectiveDate',
                headerName: 'Effective Date',
                field: 'effectiveDate',
                sortable: true,
                minWidth: 120,
                sort: { direction: 'desc', priority: 0 },
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
            });

            gridOptions.columnDefs.push({
                colId: 'ExpirationDate',
                headerName: 'Expiration Date',
                field: 'expirationDate',
                sortable: true,
                minWidth: 120,
                sort: { direction: 'desc', priority: 0 },
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
            });
        }

        gridOptions.columnDefs.push({
            colId: 'IsQuickRentalContract',
            headerName: 'Quick Rental',
            field: 'isQuickRentalContract',
            sortable: true,
            flex: 0,
            maxWidth: 140,
            filter: 'selectFilter',
            floatingFilter: true,
            floatingFilterComponent: 'selectFloatingFilter',
            filterParams: {
                suppressFilterButton: true,
                options: quickRentalOptions,
                optionsLabel: 'label',
                optionsValue: 'value',
                initialFilterValue: null,
            },
            floatingFilterComponentParams: {
                suppressFilterButton: true,
                options: quickRentalOptions,
                optionsLabel: 'label',
                optionsValue: 'value',
                initialFilterValue: null,
            },
            valueFormatter: ({ value }) => {
                if (value == null) return '';
                return value ? 'Yes' : 'No';
            },
        });

        gridOptions.columnDefs.push({
            colId: 'CreatedOn',
            headerName: 'Created On',
            field: 'createdOn',
            sortable: true,
            minWidth: 120,
            filter: 'agDateColumnFilter',
            filterParams: DateFilterDefaults,
        });

        gridOptions.columnDefs.push({
            colId: 'ContractStatus.AddJobOrProject',
            flex: 0,
            maxWidth: 150,
            headerName: '',
            field: 'status',
            sortable: false,
            className: 'test',
            cellRenderer: 'permissionButtonRenderer',
            cellRendererParams: (data) => {
                const { tenantSettings } = this.context.tenant;
                const isUsingProjects = tenantSettings?.useContractProjects === 2;
                const hasJobRelatedCustomForms = data?.data?.hasJobRelatedCustomForms;
                const viewProjects = isUsingProjects && !hasJobRelatedCustomForms;
                return {
                    clicked: viewProjects ? this.onViewProjects : this.onAddJob,
                    idField: 'id',
                    dataField: ['isProjectContract', 'number', 'hasJobRelatedCustomForms'],
                    buttonText: viewProjects ? 'View Projects' : getButtonLabel,
                    icon: viewProjects ? faFolderOpen : getIcon,
                    action: 'create',
                    category: 'job',
                    renderCondition: (context) => context.props.data?.status === 'Active'
                            && !context.props.data?.isSelfDispatch,
                };
            },
        });


        const dataSource = createDataSource(
            ApiRoutes.contract.search(),
            gridOptions,
        );

        this.setState({
            loading: false,
            gridOptions,
            dataSource,
            isAdmin: user.isAdmin,
        });
    }

    render() {
        if (this.state.loading) return <Progress />;

        const { rowData, gridOptions } = this.state;

        const exportContractPermissions = this.context.checkUserHasPermission(ApplicationPermissions.contract_export);
        const enableContractCreation = Boolean(
            this.context.tenant?.tenantSettings?.enableContractCreation,
        );
        return (
            <PageWrap>
                <PageHeading>
                    <FontAwesomeIcon
                        icon={faAddressCard}
                        className="mr-2 text-muted"
                    />
                    <span>Contracts</span>
                </PageHeading>
                <DataGridToolbar
                    entity="contract"
                    hideExcelButton
                    hideAdd={!enableContractCreation}
                    gridOptions={this.state.gridOptions}
                    serverExport={{
                        apiPath: ApiRoutes.contract.searchExport(),
                        filePrefix: 'Contracts',
                    }}
                    gridApi={this.state.gridApi}
                    dataSource={this.state.dataSource}
                    onAdd={() => this.context.history.push(`${AppNavPaths.ContractNew}`)}
                    addLabel="Add Contract"
                    serverExportDisabled={!exportContractPermissions}
                />
                <DataGrid
                    domLayout="normal"
                    rowData={rowData}
                    gridOptions={gridOptions}
                    gridStatus={this.state.gridStatus}
                />
            </PageWrap>
        );
    }
}
export default ContractIndex;
